/**
 * PaymentElementsForm.tsx — Stripe Payment Element for SetupIntent confirmation.
 *
 * Card PAN/CVC never enter React state — only Stripe Elements handles them.
 * Confirms via `stripe.confirmSetup` then hands `setupIntentId` to the caller
 * for Orders finalisation. Does not send PaymentMethod ids or amounts. A 3D
 * Secure check that REDIRECTS never comes back to this form: it comes back to
 * `returnUrl`, and CheckoutFlow finishes it there (`setupRedirectReturn.ts`).
 */
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useState } from 'react';
import { Button } from '../ui/kit';
import { getStripePromise } from './stripeClient';
import { alreadySucceededSetupIntentId, mapStripePaymentError } from './paymentErrors';

type InnerProps = {
  busy: boolean;
  confirmLabel: string;
  confirmingLabel: string;
  /**
   * A gate OUTSIDE this form that must be satisfied before the card may be
   * confirmed — today, the medication-safety acknowledgement on the payment
   * screen (2026-08-19).
   *
   * It exists because the card form is not always the only pay control: once
   * Elements has mounted, the confirm button IS the way money is committed, so a
   * gate that only disabled the caller's own button would leave this one live.
   * `blockedReasonId` points at the caller's paragraph explaining why, so the
   * block is announced in words rather than being a dead button (WCAG 3.3.1).
   */
  blocked?: boolean;
  blockedReasonId?: string;
  onConfirmed: (setupIntentId: string) => void | Promise<void>;
  /**
   * Stripe refused the confirm because this SetupIntent has ALREADY SUCCEEDED
   * (`alreadySucceededSetupIntentId`): a card confirmed on an earlier press whose
   * attach answer was lost, or whose attach never reached orders (lost attach
   * response, journey UX review 2026-09-24). Called INSTEAD of `onError`, with the
   * intent Stripe named, and awaited inside the same in-flight window as
   * `onConfirmed` — so `onSubmittingChange` still covers the attach it starts.
   *
   * The CALLER decides what the id means, because only the caller knows which
   * intent its current session holds: the same one → attach it again; any other
   * → do not attach it. Absent → today's error, unchanged.
   */
  onAlreadyConfirmed?: (setupIntentId: string) => void | Promise<void>;
  onError: (message: string) => void;
  /**
   * Reports when a confirm is IN FLIGHT — from the press, through
   * `stripe.confirmSetup` and the caller's own `onConfirmed` (the attach), to
   * its end on every outcome (2026-09-23).
   *
   * WHY IT IS LIFTED. `submitting` was private to this form, so for the whole
   * of `confirmSetup` the caller believed nothing was happening: its own
   * `busy` only rises once `onConfirmed` starts the attach. The merged checkout
   * step needs the truth to LOCK the delivery-address "Change" control — an
   * address edited while a confirm is running would be finalised against the
   * session (and the address) captured when the button was pressed, while the
   * screen showed the new one.
   *
   * Called directly from the handler rather than from an effect on
   * `submitting`, so the `false` still reaches the caller when this form has
   * already unmounted by the time the confirm settles (the caller swaps screens
   * on a successful attach); an effect would never run on an unmounted form.
   */
  onSubmittingChange?: (submitting: boolean) => void;
  /**
   * Where the bank sends the patient back to after a 3D Secure check that needs
   * a full-page REDIRECT rather than a modal (2026-09-25). `confirmSetup`'s
   * promise never answers on that path — its document is gone — so this address
   * is the ONLY thing that survives the round trip, and it must identify the
   * checkout on its own: the caller builds it with `checkoutReturnUrl`
   * (`setupRedirectReturn.ts`), which carries the assessment in the fragment.
   * Absent → the page's own address, as before.
   */
  returnUrl?: string;
};

function PaymentElementsInner({
  busy,
  confirmLabel,
  confirmingLabel,
  blocked = false,
  blockedReasonId,
  onConfirmed,
  onAlreadyConfirmed,
  onError,
  onSubmittingChange,
  returnUrl,
}: InnerProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  // `pending` = waiting on something (Stripe loading, a request in flight);
  // `locked` = must not fire. They are separated because a blocked form is NOT
  // busy, and telling assistive tech the card field is loading when it is simply
  // waiting on a tick elsewhere on the page is a lie about what to do next.
  const pending = busy || submitting || !stripe || !elements;
  const locked = pending || blocked;

  async function handleConfirm() {
    if (!stripe || !elements || locked) return;
    setSubmitting(true);
    onSubmittingChange?.(true);
    onError('');
    try {
      const result = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
        confirmParams: {
          // A modal 3D Secure check answers in place and continues below. A
          // REDIRECT check leaves this document and comes back to `return_url`
          // with Stripe's `setup_intent` / `redirect_status` parameters, where
          // CheckoutFlow finishes the order (see setupRedirectReturn.ts).
          return_url: returnUrl ?? (typeof window !== 'undefined' ? window.location.href : 'https://localhost/'),
        },
      });
      if (result.error) {
        // "Already succeeded" is not a failure of THIS card: it was set up on an
        // earlier press. Mapping it as one printed "not charged" over a hold that
        // may stand. Everything else keeps its mapped message.
        const alreadyConfirmed = alreadySucceededSetupIntentId(result.error);
        if (alreadyConfirmed && onAlreadyConfirmed) {
          await onAlreadyConfirmed(alreadyConfirmed);
          return;
        }
        onError(mapStripePaymentError(result.error).message);
        return;
      }
      const setupIntent = result.setupIntent;
      if (!setupIntent?.id || setupIntent.status !== 'succeeded') {
        onError(
          mapStripePaymentError({
            code: setupIntent?.status === 'requires_action' ? 'authentication_required' : undefined,
          }).message,
        );
        return;
      }
      await onConfirmed(setupIntent.id);
    } catch {
      onError(mapStripePaymentError({ type: 'api_connection_error' }).message);
    } finally {
      setSubmitting(false);
      onSubmittingChange?.(false);
    }
  }

  return (
    <div>
      <div
        style={{ marginBottom: 'var(--gps-s-3)' }}
        data-testid="stripe-payment-element"
        aria-busy={pending}
      >
        <PaymentElement
          options={{
            layout: 'tabs',
          }}
        />
      </div>
      <Button
        type="button"
        variant="cta"
        disabled={locked}
        aria-busy={submitting || busy}
        aria-disabled={locked}
        aria-describedby={blocked ? blockedReasonId : undefined}
        onClick={() => void handleConfirm()}
      >
        {submitting || busy ? confirmingLabel : confirmLabel}
      </Button>
    </div>
  );
}

export function PaymentElementsForm({
  publishableKey,
  clientSecret,
  busy,
  confirmLabel,
  confirmingLabel,
  blocked,
  blockedReasonId,
  onConfirmed,
  onAlreadyConfirmed,
  onError,
  onSubmittingChange,
  returnUrl,
}: {
  publishableKey: string;
  clientSecret: string;
  busy: boolean;
  confirmLabel: string;
  confirmingLabel: string;
  /** See {@link InnerProps.blocked} — an outside gate on committing the card. */
  blocked?: boolean;
  blockedReasonId?: string;
  onConfirmed: (setupIntentId: string) => void | Promise<void>;
  /** See {@link InnerProps.onAlreadyConfirmed} — Stripe says this card is already set up. */
  onAlreadyConfirmed?: (setupIntentId: string) => void | Promise<void>;
  onError: (message: string) => void;
  /** See {@link InnerProps.onSubmittingChange} — a confirm is in flight. */
  onSubmittingChange?: (submitting: boolean) => void;
  /** See {@link InnerProps.returnUrl} — where a redirect 3D Secure check comes back to. */
  returnUrl?: string;
}) {
  const stripePromise = getStripePromise(publishableKey);
  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <PaymentElementsInner
        busy={busy}
        confirmLabel={confirmLabel}
        confirmingLabel={confirmingLabel}
        blocked={blocked}
        blockedReasonId={blockedReasonId}
        onConfirmed={onConfirmed}
        onAlreadyConfirmed={onAlreadyConfirmed}
        onError={onError}
        onSubmittingChange={onSubmittingChange}
        returnUrl={returnUrl}
      />
    </Elements>
  );
}
